import React, { Component } from 'react';
import { AlertCircleIcon, RefreshCwIcon, HomeIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
interface Props {
  children: React.ReactNode;
}
interface State {
  hasError: boolean;
  error: Error | null;
}
// Separate ErrorFallback into its own component
const ErrorFallback = ({
  error,
  onReset
}: {
  error: Error | null;
  onReset: () => void;
}) => {
  const navigate = useNavigate();
  return <div className="min-h-screen bg-surface flex items-center justify-center p-4" data-id="element-385">
      <div className="bg-surface-light border border-surface-border rounded-none p-6 max-w-lg w-full" data-id="element-386">
        <div className="flex items-start gap-4" data-id="element-387">
          <div className="p-3 bg-status-error/10 rounded-none" data-id="element-388">
            <AlertCircleIcon size={24} className="text-status-error" data-id="element-389" />
          </div>
          <div className="flex-1" data-id="element-390">
            <h1 className="text-lg font-medium text-white font-mono mb-2" data-id="element-391">
              Something went wrong
            </h1>
            <div className="bg-surface p-4 rounded-none border border-surface-border mb-4" data-id="element-392">
              <code className="text-sm text-gray-400 font-mono" data-id="element-393">
                {error?.message || 'An unexpected error occurred'}
              </code>
            </div>
            <div className="flex flex-col sm:flex-row gap-3" data-id="element-394">
              <button onClick={onReset} className="flex items-center justify-center gap-2 px-4 py-2 bg-accent 
                         hover:bg-accent-light text-white rounded-none transition-colors" aria-label="Try again" data-id="element-395">
                <RefreshCwIcon size={16} data-id="element-396" />
                <span data-id="element-397">Try Again</span>
              </button>
              <button onClick={() => navigate('/')} className="flex items-center justify-center gap-2 px-4 py-2 border 
                         border-surface-border text-gray-400 hover:text-white 
                         rounded-none transition-colors" aria-label="Return to home page" data-id="element-398">
                <HomeIcon size={16} data-id="element-399" />
                <span data-id="element-400">Return Home</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }
  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    // Here you would typically log to your error reporting service
  }
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null
    });
  };
  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} onReset={this.handleReset} data-id="element-401" />;
    }
    return this.props.children;
  }
}